import { useMutation, MutationFunction, MutationOptions } from 'react-query';
import {
    CreateFormSectionBody,
    UpdateFormSectionBody,
    MoveFormSectionBody,
    FormSection,
    createFormSection,
    duplicateFormSection,
    updateFormSection,
    moveFormSection,
    deleteFormSection,
    deleteFormSectionWithContent,
    FormSectionResponse,
} from '../../services/apiSLTP/qhse/forms/formSections';

// --- CREATE ---
type UseCreateFormSectionMutationOptions = MutationOptions<FormSectionResponse, Error, CreateFormSectionBody>;

export const useCreateFormSectionMutation = (options?: UseCreateFormSectionMutationOptions) => {
    const mutationFn: MutationFunction<FormSectionResponse, CreateFormSectionBody> = async (data) => {
        try {
            return await createFormSection(data);
        } catch (error) {
            throw new Error('An unexpected error occurred during formSection creation');
        }
    };

    return useMutation(mutationFn, options);
};

// --- DUPLICATE ---
type UseDuplicateFormSectionMutationOptions = MutationOptions<FormSectionResponse, Error, { sectionId: string }>;

export const useDuplicateFormSectionMutation = (options?: UseDuplicateFormSectionMutationOptions) => {
    const mutationFn: MutationFunction<FormSectionResponse, { sectionId: string }> = async ({ sectionId }) => {
        try {
            return await duplicateFormSection(sectionId);
        } catch (error) {
            throw new Error('An unexpected error occurred during formSection duplication');
        }
    };

    return useMutation(mutationFn, options);
};

// --- UPDATE ---
type UseUpdateFormSectionMutationOptions = MutationOptions<FormSectionResponse, Error, { sectionId: string; data: UpdateFormSectionBody }>;

export const useUpdateFormSectionMutation = (options?: UseUpdateFormSectionMutationOptions) => {
    const mutationFn: MutationFunction<FormSectionResponse, { sectionId: string; data: UpdateFormSectionBody }> = async ({ sectionId, data }) => {
        try {
            return await updateFormSection(sectionId, data);
        } catch (error) {
            throw new Error('An unexpected error occurred during formSection update');
        }
    };

    return useMutation(mutationFn, options);
};

// --- MOVE ---
type UseMoveFormSectionMutationOptions = MutationOptions<FormSectionResponse, Error, { sectionId: string; data: MoveFormSectionBody }>;

export const useMoveFormSectionMutation = (options?: UseMoveFormSectionMutationOptions) => {
    const mutationFn: MutationFunction<FormSectionResponse, { sectionId: string; data: MoveFormSectionBody }> = async ({ sectionId, data }) => {
        try {
            return await moveFormSection(sectionId, data);
        } catch (error) {
            throw new Error('An unexpected error occurred during formSection move');
        }
    };

    return useMutation(mutationFn, options);
};

// --- DELETE (only) ---
type UseDeleteFormSectionMutationOptions = MutationOptions<{ message: string }, Error, { sectionId: string }>;

export const useDeleteFormSectionMutation = (options?: UseDeleteFormSectionMutationOptions) => {
    const mutationFn: MutationFunction<{ message: string }, { sectionId: string }> = async ({ sectionId }) => {
        try {
            return await deleteFormSection(sectionId);
        } catch (error) {
            throw new Error('An unexpected error occurred during formSection deletion');
        }
    };

    return useMutation(mutationFn, options);
};

// --- DELETE WITH CONTENT ---
type UseDeleteFormSectionWithContentMutationOptions = MutationOptions<{ message: string }, Error, { sectionId: string }>;

export const useDeleteFormSectionWithContentMutation = (options?: UseDeleteFormSectionWithContentMutationOptions) => {
    const mutationFn: MutationFunction<{ message: string }, { sectionId: string }> = async ({ sectionId }) => {
        try {
            return await deleteFormSectionWithContent(sectionId);
        } catch (error) {
            throw new Error('An unexpected error occurred during formSection deletion with content');
        }
    };

    return useMutation(mutationFn, options);
};
